import { useMutation, UseMutationResult } from "@tanstack/react-query"
import ResponseError from "#/types/responseError"
import { carrotClient } from "#/utils/CarrotClient"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import { PreAuthFormState } from "#/components/carrot-card-cost-share/state/CarrotCardCostSharePreAuthContext"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { JourneyTypes } from "#/components/get-reimbursed/steps/ExpenseJourneyTypes"
import CurrencyCodesToAllowedDecimalPlacesMap from "#/utils/CurrencyCode"

export interface PreAuthAnswersPayload {
  journeyType: string
  whoReceivedCare: string
  isStorageFees: string
  isMedicationExpenses: boolean
  journeySubType: string
  isInfertilityDx: boolean
  isMedicallyNecessary: boolean
  isStepchildAdoption: boolean
  totalCost: number
  currencyCode: string
}

export interface PreAuthAnswersResponse {
  preAuthAnswersId: number
  coveredSwipeAmount: number
  memberOOPAmount: number
  currencyCode: string
  currencyDivisor: number
  expirationDate: string
  status: "APPROVED" | "DENIED"
  deniedReason: "UNAUTHORIZED_QME" | "NO_FUNDS_AVAILABLE" | null
}

function parseYesNoToBoolean(value: string): boolean {
  return !value || value === "NO" ? false : true
}

function getJourneyType(state: PreAuthFormState): string {
  if (state.journeyType === JourneyTypes.FERTILITY_CARE) {
    return state.whatKindFertilityCare === "FERTILITY_TESTING" ||
      state.whatKindFertilityCare === "OTHER_FERTILITY_TREATMENTS"
      ? JourneyTypes.PATHFINDING
      : state.whatKindFertilityCare
  }
  return state.journeyType
}

export function usePreAuthAnswers(): UseMutationResult<PreAuthAnswersResponse, ResponseError, PreAuthFormState> {
  const {
    company: { currencyCode }
  } = useCurrentUser()
  return useMutation<PreAuthAnswersResponse, ResponseError, PreAuthFormState>(
    (state: PreAuthFormState) => {
      const payload: PreAuthAnswersPayload = {
        journeyType: getJourneyType(state),
        whoReceivedCare: state.whoReceivedCare,
        isStorageFees: state.isStorageFees,
        isMedicationExpenses: parseYesNoToBoolean(state.isMedicationExpenses),
        journeySubType: state.journeySubType,
        isInfertilityDx: parseYesNoToBoolean(state.isInfertilityDx),
        isStepchildAdoption: parseYesNoToBoolean(state.isStepchildAdoption),
        isMedicallyNecessary: parseYesNoToBoolean(state.isMedicallyNecessary),
        totalCost: convertToSmallestCurrencyUnit(state.totalCost, currencyCode),
        currencyCode
      }
      return carrotClient.submitPreAuthAnswers(payload)
    },
    {
      retry: buildCarrotApiRetryPolicy(3)
    }
  )
}

const convertToSmallestCurrencyUnit = (amount: number, currencyCode: string): number => {
  const currencyCodeUpperCase = currencyCode.toUpperCase() as keyof typeof CurrencyCodesToAllowedDecimalPlacesMap
  const digits = CurrencyCodesToAllowedDecimalPlacesMap[currencyCodeUpperCase]
  // Use Math.round to handle floating-point precision issues
  return Math.round(amount * Math.pow(10, digits))
}
